/**
 * Network measurement read API (assayer-backend `api/analytics.py`, mig 314).
 *
 * Four operator-authed endpoints; the "public" one is public-SAFE (it reads
 * the `v_public_metric_facts` view), not publicly reachable (D12). Series
 * values arrive as STRINGS — NUMERIC through the proxy chain — so every
 * consumer goes through `toNumber` in `analyticsShape.ts`.
 *
 * Multi-value `site` must serialise as `site=a&site=b` (FastAPI `List[str]`),
 * not axios's default `site[]=a` — hence `paramsSerializer.indexes: null`.
 */
import { apiClient } from './config.js';
import type { MetricCoverage } from '../analyticsShape.js';

export type MetricVisibility = 'internal' | 'public_safe';
export type MetricBasis = 'daily' | 'rolling_30d' | 'snapshot_24h' | 'point_in_time' | 'monthly';
export type IndicatorClass = 'leading' | 'lagging' | 'neutral';
export type MetricCollector = 'cloudflare' | 'gsc' | 'buffer' | 'linkedin' | 'internal';

export interface MetricDefinition {
  metric_key: string;
  display_name: string;
  unit: string;
  basis: MetricBasis;
  visibility: MetricVisibility;
  indicator_class: IndicatorClass;
  collector: MetricCollector;
  notes: string | null;
  /** Small-cell floor for the public view (mig 374); NUMERIC → string on the wire. */
  public_min_value?: string | number | null;
}

export interface MetricCatalog {
  metrics: MetricDefinition[];
  internal_count: number;
  public_safe_count: number;
}

export interface SeriesRow {
  metric_key: string;
  site: string;
  /** YYYY-MM-DD */
  metric_date: string;
  dimension_key: string;
  dimension_value: string;
  /** NUMERIC — a string on the wire. */
  value: string | number;
  is_estimated: boolean;
  visibility: MetricVisibility;
  basis: MetricBasis;
  display_name: string;
  unit: string;
}

export interface SeriesResponse {
  metric: string;
  dimension_key: string;
  from: string;
  to: string;
  rows: SeriesRow[];
  estimated_rows: number;
}

export interface SeriesParams {
  metric: string;
  sites?: string[];
  dateFrom: string;
  dateTo: string;
  dimension?: string;
}

const FLAT = { indexes: null as null };

export async function getMetricCatalog(): Promise<MetricCatalog> {
  const { data } = await apiClient.get<MetricCatalog>('/analytics/network/catalog');
  return data;
}

export async function getMetricSeries(p: SeriesParams): Promise<SeriesResponse> {
  const { data } = await apiClient.get<SeriesResponse>('/analytics/network', {
    params: {
      metric: p.metric,
      site: p.sites,
      date_from: p.dateFrom,
      date_to: p.dateTo,
      dimension: p.dimension ?? 'total',
    },
    paramsSerializer: FLAT,
  });
  return data;
}

/** Record a figure that has no API behind it (R5: the X follower count). */
export async function recordManualMetric(p: { metricKey: string; site: string; value: number; metricDate?: string }): Promise<void> {
  await apiClient.post('/analytics/network/manual', {
    metric_key: p.metricKey, site: p.site, value: p.value, metric_date: p.metricDate ?? null,
  });
}

/** When each metric's collection starts — first stored day, first non-zero day, latest day. */
export async function getMetricCoverage(): Promise<MetricCoverage[]> {
  const { data } = await apiClient.get<{ metrics: MetricCoverage[] }>('/analytics/network/coverage');
  return data.metrics ?? [];
}

/** LinkedIn Standardized Data labels (mig 374): {kind: {id: name}}. */
export type FacetLabels = Record<string, Record<string, string>>;

export async function getFacetLabels(): Promise<FacetLabels> {
  const { data } = await apiClient.get<{ labels: FacetLabels }>('/analytics/network/labels');
  return data.labels ?? {};
}

export interface CsvParams {
  sites?: string[];
  dateFrom: string;
  dateTo: string;
  publicOnly?: boolean;
}

/** The weekly dataset, as the backend renders it. Goes through the API
 *  client so the operator token travels — a bare <a href> cannot. */
export async function downloadNetworkCsv(p: CsvParams): Promise<Blob> {
  const { data } = await apiClient.get<Blob>('/analytics/network/export.csv', {
    params: { site: p.sites, date_from: p.dateFrom, date_to: p.dateTo, public_only: p.publicOnly ?? false },
    paramsSerializer: FLAT,
    responseType: 'blob',
  });
  return data;
}
